import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { iproyalStickyOf, proxyPasswordIssue } from "@match/shared";
import { AccountConflictNote } from "@/components/account-conflict";
import { ApiError, api, errorMessage, type AccountPatch } from "@/lib/api";
import { date, money } from "@/lib/format";
import { fractionToPct, parseDecimal, pctIssue, pctToFraction } from "@/lib/units";
import { keys, useBrokers, usePropFirms, useRefreshDirectory, useServers } from "@/lib/queries";
import type { Account, AccountConflict, Decimal, LedgerRow, ProxyCheck, ProxyPublic } from "@/lib/types";
import {
  Button,
  COMPUTER_ID_RE,
  ErrorNote,
  Field,
  FieldCheckContext,
  SidePanel,
  inputClass,
  useIsAdmin,
  useIsClient,
} from "@/components/ui";

function proxyName(p: Pick<ProxyPublic, "label" | "host" | "port">): string {
  return p.label ? `${p.label} · ${p.host}:${p.port}` : `${p.host}:${p.port}`;
}

/** Stringa vuota -> null: i campi facoltativi si svuotano, non si salvano come "". */
const orNull = (v: string) => (v.trim() === "" ? null : v.trim());

/**
 * Password del cliente: si GENERA, non si digita.
 *
 * `crypto.getRandomValues` e non `Math.random`, che è prevedibile. Niente
 * caratteri ambigui (0/O, 1/l/I): questa password viene letta ad alta voce o
 * copiata a mano, e una «l» scambiata per «1» diventa una telefonata.
 */
const PASSWORD_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
function generatePassword(length = 20): string {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => PASSWORD_ALPHABET[b % PASSWORD_ALPHABET.length]).join("");
}

// ---------------------------------------------------------------------------
// I campi che mancano
// ---------------------------------------------------------------------------

/**
 * Una regola del form: il nome del campo (la sua etichetta, o il `check` del
 * `Field`) e se adesso va bene. `false`/`null` = la regola qui non vale.
 */
export type FieldRule = readonly [field: string, ok: boolean] | false | null | undefined;

/** Il primo dei campi nominati, nell'ordine della pagina: ci si scorre e gli si da' il fuoco. */
function goToFirst(form: string, keys: string[]) {
  const els = keys
    .map((k) => document.querySelector<HTMLElement>(`[data-form="${CSS.escape(form)}"][data-check="${CSS.escape(k)}"]`))
    .filter((el): el is HTMLElement => el !== null)
    .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  const first = els[0];
  if (!first) return;
  first.scrollIntoView({ behavior: "smooth", block: "center" });
  first
    .querySelector<HTMLElement>("input:not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])")
    ?.focus({ preventScroll: true });
}

/** La validita' nativa di un tipo di campo (email, url), senza il fumetto del browser: la stessa regola del `type`. */
export function nativeValid(type: "email" | "url", value: string): boolean {
  const i = document.createElement("input");
  i.type = type;
  i.value = value;
  return i.checkValidity();
}

/**
 * IL FORM NON RESTA MUTO. Un helper solo per tutti i form di creazione.
 *
 * Il pulsante resta cliccabile (spento solo mentre la richiesta e' in corso): al
 * clic `attempt()` dice se si puo' mandare; se no segna di rosso i campi che non
 * vanno, scorre al primo e gli da' il fuoco, e `MissingNote` scrive sotto il
 * pulsante «Mancano: …». Le regole sono quelle che il form aveva gia': qui si
 * mostrano, non se ne aggiungono. Il rosso segue la regola, quindi sparisce
 * appena il campo e' compilato bene.
 *
 * `fromError`: un 4xx che nomina un campo (i `fields` della validazione, o un
 * `code` come `LOGIN_TAKEN`) lo segna allo stesso modo; il messaggio resta
 * quello di sempre. Quel rosso si toglie quando il campo cambia.
 */
export function useFormCheck(rules: FieldRule[]) {
  const form = useId();
  const [tried, setTried] = useState(false);
  const [fromServer, setFromServer] = useState<string[]>([]);
  const failing = rules.filter((r): r is readonly [string, boolean] => !!r && !r[1]).map(([field]) => field);
  const missing = tried ? failing : [];
  const touch = useCallback((key: string) => setFromServer((s) => (s.includes(key) ? s.filter((k) => k !== key) : s)), []);
  /** Il pannello si riapre pulito. */
  const reset = useCallback(() => {
    setTried(false);
    setFromServer([]);
  }, []);
  // Un oggetto nuovo a ogni giro, come le regole: i `Field` si ridisegnano comunque col form che li contiene.
  const ctx = { form, bad: new Set([...missing, ...fromServer]), touch };
  return {
    missing,
    ctx,
    /** Al clic: vero = si manda; falso = i campi sono segnati e il fuoco e' sul primo. */
    attempt: (): boolean => {
      setTried(true);
      setFromServer([]);
      if (!failing.length) return true;
      goToFirst(form, failing);
      return false;
    },
    /** `fields`: path della validazione o `code` del backend -> nome del campo. */
    fromError: (e: unknown, fields: Record<string, string>) => {
      if (!(e instanceof ApiError) || e.status < 400 || e.status >= 500) return;
      const keys = [...new Set([...e.fields.map((f) => fields[f.path]), e.code ? fields[e.code] : undefined].filter((k): k is string => !!k))];
      if (!keys.length) return;
      setFromServer(keys);
      goToFirst(form, keys);
    },
    reset,
  };
}
export type FormCheckState = ReturnType<typeof useFormCheck>;

/** I campi del form che controlla: senza, i `Field` non si segnano. Non aggiunge elementi alla pagina. */
export function FormCheck({ check, children }: { check: FormCheckState; children: ReactNode }) {
  return <FieldCheckContext.Provider value={check.ctx}>{children}</FieldCheckContext.Provider>;
}

/** La riga sotto il pulsante: «Mancano: Conto broker, Lotti». `end` nei piedi dei pannelli, allineati a destra. */
export function MissingNote({ check, end = false }: { check: FormCheckState; end?: boolean }) {
  const m = check.missing;
  if (!m.length) return null;
  return (
    <p role="alert" className={`w-full text-xs text-loss ${end ? "text-right" : ""}`}>
      {m.length === 1 ? "Manca" : "Mancano"}: {m.join(", ")}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Cliente
// ---------------------------------------------------------------------------

export interface ClientFormValue {
  id: string;
  name: string;
  email: string;
  notes: string | null;
  computerId: string;
  defaultProxyId: string | null;
  defaultProxy: ProxyPublic | null;
  /** Le due quote predefinite, come FRAZIONI. Il ciclo se le congela alla nascita. */
  defaultPayoutSplitPct: Decimal;
  defaultBurnSplitPct: Decimal;
}

/**
 * Il cliente si crea COMPLETO: anagrafica, il suo proxy e il suo CID.
 *
 * Un proxy per cliente (DECISIONE 19), quindi non c'è più un pool da cui
 * pescare e non c'è più una pagina Proxy: i campi del proxy stanno qui, e il
 * legame lo crea il backend insieme alla riga.
 *
 * I parametri del gateway — paese, città, sessione, durata — sono campi loro e
 * non suffissi dentro la password: su IPRoyal ci vivevano dentro, e per sapere
 * da che paese esce un cliente bisognava svelare il segreto.
 */
export function ClientPanel({
  open,
  client,
  onClose,
  onSaved,
}: {
  open: boolean;
  client: ClientFormValue | null;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const refresh = useRefreshDirectory();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [cid, setCid] = useState("");
  // Le percentuali si SCRIVONO come percentuali e viaggiano come frazioni: la
  // conversione sta in lib/units.ts e succede solo li'.
  const [payoutSplit, setPayoutSplit] = useState("70");
  const [burnSplit, setBurnSplit] = useState("80");

  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [type, setType] = useState<"SOCKS5" | "HTTP" | "HTTPS">("SOCKS5");
  const [provider, setProvider] = useState("");
  const [username, setUsername] = useState("");
  const isAdmin = useIsAdmin();
  /** La password salvata del proxy, letta in chiaro: resta a video finche' il modulo e' aperto. */
  const [shownPassword, setShownPassword] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [proxyCheck, setProxyCheck] = useState<ProxyCheck | null>(null);
  const [checking, setChecking] = useState(false);

  // Accesso del cliente: solo alla CREAZIONE, e solo se lo si vuole.
  // Nessuna email sua: l'utente CLIENT prende sempre quella del cliente.
  const [accessWanted, setAccessWanted] = useState(false);
  const [accessPassword, setAccessPassword] = useState("");
  /** La password appena creata, mostrata UNA volta: nel database c'è l'hash. */
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);
  // Conti ancora collegati col proxy di prima, dopo il salvataggio: il pannello resta aperto per ricollegarli.
  const [stale, setStale] = useState<{ proxyId: string; clientId: string; count: number; esito: string | null } | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const proxy = client?.defaultProxy ?? null;
  const portNum = Number(port);
  const proxyWanted = host.trim() !== "" || port.trim() !== "";
  // La stessa regola del backend (400 al salvataggio), detta mentre si scrive.
  const passwordIssue = proxyPasswordIssue(provider, password);
  const check = useFormCheck([
    ["Nome", name.trim() !== ""],
    ["Email", email.trim() !== "" && nativeValid("email", email.trim())],
    // Una quota non capita (`abc`, per esempio) non si omette in silenzio: senza avviso il predefinito resterebbe quello di prima.
    ["Sui payout (%)", !pctIssue("Quota sui payout", payoutSplit)],
    ["Alla bruciatura (%)", !pctIssue("Quota alla bruciatura", burnSplit)],
    ["Host", !proxyWanted || host.trim() !== ""],
    ["Porta", !proxyWanted || (Number.isInteger(portNum) && portNum >= 1 && portNum <= 65535)],
    ["Password del proxy", !passwordIssue],
  ]);
  const resetCheck = check.reset;

  useEffect(() => {
    if (!open) return;
    setName(client?.name ?? "");
    setEmail(client?.email ?? "");
    setAccessWanted(false);
    setAccessPassword("");
    setCreated(null);
    setStale(null);
    setNotes(client?.notes ?? "");
    setCid(client?.computerId ?? "");
    setPayoutSplit(client ? fractionToPct(client.defaultPayoutSplitPct) : "70");
    setBurnSplit(client ? fractionToPct(client.defaultBurnSplitPct) : "80");
    setHost(proxy?.host ?? "");
    setPort(proxy ? String(proxy.port) : "");
    setType(proxy?.type ?? "SOCKS5");
    setProvider(proxy?.provider ?? "iproyal");
    setUsername(proxy?.username ?? "");
    setPassword("");
    setCountry(proxy?.country ?? "");
    setCity(proxy?.city ?? "");
    setProxyCheck(null);
    setShownPassword(null);
    setRevealError(null);
    setError(null);
    resetCheck();
  }, [open, client, proxy, resetCheck]);

  // Senza l'email del cliente l'accesso non ha da dove prendere la sua: la
  // casella si disattiva invece di lasciar spuntare qualcosa che poi il
  // salvataggio rifiuterebbe.
  const accessDisabled = !client && email.trim() === "";
  useEffect(() => {
    if (accessDisabled && accessWanted) setAccessWanted(false);
  }, [accessDisabled, accessWanted]);

  const cidInvalid = cid !== "" && !COMPUTER_ID_RE.test(cid);
  const proxyInvalid = proxyWanted && (host.trim() === "" || !Number.isInteger(portNum) || portNum < 1 || portNum > 65535);
  /*
   * LA STRINGA STICKY DEL PANNELLO IPROYAL si usa com'e': paese, citta' e
   * sessione sono quelli dentro la stringa, e qui si mostrano in una riga sola
   * (`StickyLine`), non come campi. Quella appena scritta, o — password vuota
   * in modifica — quella salvata.
   */
  const salvati = useQuery({ queryKey: ["proxies"], queryFn: () => api.proxies.list(), enabled: open && !!proxy });
  const stickySalvata = password ? null : (salvati.data?.find((p) => p.id === proxy?.id)?.sticky ?? null);
  const sticky = iproyalStickyOf(provider, password) ?? (provider.trim().toLowerCase() === "iproyal" ? stickySalvata : null);

  const mostraPassword = async () => {
    if (!proxy) return;
    setRevealing(true);
    setRevealError(null);
    try {
      setShownPassword((await api.proxies.revealPassword(proxy.id)).password);
    } catch (e) {
      setRevealError(errorMessage(e));
    } finally {
      setRevealing(false);
    }
  };

  // Il controllo sui dati del modulo, prima di salvarli: la password va e basta.
  const controllaProxy = async () => {
    setChecking(true);
    setProxyCheck(null);
    setError(null);
    try {
      const r = await api.proxies.checkDraft({
        provider: orNull(provider),
        host: host.trim(),
        port: portNum,
        type,
        username: orNull(username),
        country: sticky ? sticky.country : orNull(country),
        city: sticky ? sticky.city : orNull(city),
        ...(password ? { password } : {}),
        ...(client ? { clientId: client.id } : {}),
      });
      setProxyCheck(r.check);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setChecking(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!check.attempt()) return;
    setBusy(true);
    setError(null);
    const body = {
      name: name.trim(),
      email: email.trim(),
      notes: orNull(notes),
      ...(pctToFraction(payoutSplit) ? { defaultPayoutSplitPct: pctToFraction(payoutSplit)! } : {}),
      ...(pctToFraction(burnSplit) ? { defaultBurnSplitPct: pctToFraction(burnSplit)! } : {}),
      ...(!client && accessWanted && accessPassword ? { access: { password: accessPassword } } : {}),
    };
    try {
      const saved = client ? await api.clients.update(client.id, body) : await api.clients.create(body);

      const proxyBody = {
        provider: orNull(provider),
        host: host.trim(),
        port: portNum,
        type,
        username: orNull(username),
        // Con la stringa sticky, le colonne dicono quello che c'e' nella stringa.
        country: sticky ? sticky.country : orNull(country),
        city: sticky ? sticky.city : orNull(city),
        ...(password ? { password } : {}),
      };
      let staleSessions = 0;
      if (proxyWanted && proxy) staleSessions = (await api.proxies.update(proxy.id, proxyBody)).staleSessions;
      else if (proxyWanted) await api.proxies.create({ ...proxyBody, clientId: saved.id });
      else if (proxy) {
        // Tolto dal form: si stacca e si cancella. Un proxy senza cliente non è
        // una riga libera nel pool, perché il pool non esiste più.
        await api.clients.update(saved.id, { defaultProxyId: null });
        await api.proxies.remove(proxy.id);
      }

      await refresh();
      // La password si mostra UNA volta, e il pannello resta aperto: chiuderlo
      // subito vorrebbe dire perderla, perché nel database c'è solo l'hash.
      if (!client && accessWanted && accessPassword) {
        setCreated({ email: email.trim().toLowerCase(), password: accessPassword });
        setAccessPassword("");
        return;
      }
      if (proxy && staleSessions > 0) {
        setStale({ proxyId: proxy.id, clientId: saved.id, count: staleSessions, esito: null });
        return;
      }
      onSaved?.(saved.id);
      onClose();
    } catch (err) {
      await refresh(); // il cliente può essere stato creato prima dell'errore sul proxy
      setError(errorMessage(err));
      check.fromError(err, { name: "Nome", email: "Email", EMAIL_TAKEN: "Email", CLIENT_EMAIL_REQUIRED: "Email", host: "Host", port: "Porta", password: "Password del proxy" });
    } finally {
      setBusy(false);
    }
  };

  const ricollega = async () => {
    if (!stale) return;
    setReconnecting(true);
    setError(null);
    try {
      const r = await api.proxies.reconnect(stale.proxyId);
      const esito = [
        `Ricollegati col proxy attuale: ${r.reconnected}.`,
        r.skipped.length
          ? `Saltati perché hanno posizioni aperte (si ricollegano quando sono flat): ${r.skipped.map((s) => s.label).join(", ")}.`
          : "",
        r.failed.length ? `Non riusciti: ${r.failed.map((f) => `${f.label} (${f.error})`).join("; ")}.` : "",
      ]
        .filter(Boolean)
        .join(" ");
      setStale({ ...stale, count: r.skipped.length + r.failed.length, esito });
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setReconnecting(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={client ? "Modifica cliente" : "Nuovo cliente"}
      subtitle="Il proxy e il CID sono del cliente e valgono per i suoi conti. I conti si aggiungono dopo."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          {created ? (
            <Button variant="primary" onClick={onClose}>
              Ho copiato la password
            </Button>
          ) : stale ? (
            <Button
              variant="primary"
              onClick={() => {
                onSaved?.(stale.clientId);
                onClose();
              }}
            >
              Chiudi
            </Button>
          ) : (
            <Button variant="primary" type="submit" form="client-form" disabled={busy || cidInvalid}>
              {busy ? "Salvataggio…" : client ? "Salva" : "Crea cliente"}
            </Button>
          )}
          {!created && !stale && <MissingNote check={check} end />}
        </>
      }
    >
      <FormCheck check={check}>
      <form id="client-form" noValidate onSubmit={submit} className="space-y-4">
        <Field label="Nome">
          {(id) => <input id={id} required value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />}
        </Field>
        <Field label="Email">
          {(id) => (
            <input id={id} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
          )}
        </Field>
        {!client && (
          <fieldset className="space-y-3 rounded border border-line p-3">
            <legend className="px-1 text-sm text-muted">Accesso del cliente</legend>
            {created ? (
              <div role="status" className="space-y-2">
                <p className="text-sm text-ink">
                  Credenziali create. <strong>Si vedono solo adesso</strong>: nel database c'è soltanto l'hash, e non si
                  possono rileggere — si possono solo sostituire.
                </p>
                <dl className="space-y-1 rounded border border-line bg-surface px-3 py-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Email</dt>
                    <dd className="font-mono text-ink">{created.email}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Password</dt>
                    <dd className="select-all font-mono text-ink">{created.password}</dd>
                  </div>
                </dl>
                <p className="text-xs text-faint">
                  Al primo accesso il cliente dovrà cambiarla: una password generata da noi e comunicata su una chat non
                  resta un segreto. <strong>L'area cliente non è ancora attiva</strong>, quindi l'utente esiste ma non entra.
                </p>
              </div>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm text-muted">
                  <input
                    type="checkbox"
                    checked={accessWanted}
                    disabled={accessDisabled}
                    onChange={(e) => setAccessWanted(e.target.checked)}
                  />
                  Crea un utente per questo cliente
                </label>
                {accessDisabled && <p className="text-xs text-warn">Serve l'email del cliente</p>}
                {accessWanted && (
                  <>
                    <p className="text-xs text-faint">
                      Login: <span className="font-mono">{email.trim().toLowerCase()}</span> — sempre quella del cliente.
                    </p>
                    <Field label="Password" hint="Si genera, non si scrive: verrà mostrata una volta sola dopo il salvataggio.">
                      {(id) => (
                        <div className="flex gap-2">
                          <input id={id} readOnly value={accessPassword} placeholder="—" className={`${inputClass} flex-1 font-mono`} />
                          <Button onClick={() => setAccessPassword(generatePassword())}>Genera</Button>
                        </div>
                      )}
                    </Field>
                    {!accessPassword && <p className="text-xs text-warn">Genera la password: senza, l'utente non si crea.</p>}
                  </>
                )}
              </>
            )}
          </fieldset>
        )}

        {/*
          * IL CID NON SI DIGITA E DA QUI NON SI MODIFICA: e' un'IDENTITA'.
          *
          * Lo genera il backend alla creazione del cliente, e resta lo stesso
          * — fra fasi, fra conti prop bruciati e rifatti, fra cicli — finche'
          * qualcuno non lo cambia di proposito dalla matita nella scheda del
          * cliente, che e' una rotta sua con conferma e audit.
          * Due CID per lo stesso cliente vogliono dire, per la prop firm, due
          * macchine: la correlazione che il proxy dedicato si paga per rompere,
          * vista dall'altro lato.
          *
          * Un campo di testo qui sarebbe un CID che si puo' scrivere sbagliato,
          * e uno sbagliato non da' un errore: da' una connessione che riesce
          * presentando un dispositivo diverso. Il `PATCH` lo rifiuta comunque.
          */}
        {client && (
          <Field label="CID" hint="Generato alla creazione. Si cambia dalla matita nella scheda del cliente, con conferma: la prop firm vedrà un dispositivo nuovo.">
            {() => <p className="machine-id py-2">{cid || "—"}</p>}
          </Field>
        )}

        <fieldset className="space-y-3 rounded border border-line p-3">
          <legend className="px-1 text-sm text-muted">Quote del cliente</legend>
          <p className="text-xs text-faint">
            Sono PREDEFINITI, non regole: il ciclo se le congela alla nascita, come il CID. Cambiarle qui non tocca i cicli già
            aperti — vale dal prossimo. All'atto pratico si conferma un importo, non una percentuale: questi numeri lo propongono.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sui payout (%)" hint="Del netto che la prop firm ci versa">
              {(id) => (
                <input id={id} inputMode="decimal" value={payoutSplit} onChange={(e) => setPayoutSplit(e.target.value)} className={inputClass} />
              )}
            </Field>
            <Field label="Alla bruciatura (%)" hint="Del netto del ciclo, quando il conto prop brucia">
              {(id) => (
                <input id={id} inputMode="decimal" value={burnSplit} onChange={(e) => setBurnSplit(e.target.value)} className={inputClass} />
              )}
            </Field>
          </div>
          {(!pctToFraction(payoutSplit) || !pctToFraction(burnSplit)) && (
            <p className="text-xs text-loss">Le quote si scrivono come percentuali tra 0 e 100.</p>
          )}
        </fieldset>

        <fieldset className="space-y-3 rounded border border-line p-3">
          <legend className="px-1 text-sm text-muted">Proxy del cliente</legend>
          <p className="text-xs text-faint">
            Uno per cliente: due clienti dallo stesso indirizzo se li fa collegare la prop firm, ed è la cosa che il proxy dedicato
            esiste per evitare. Host e porta vuoti: nessun proxy, e allora i conti prop non si connettono.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Host">
                {(id) => (
                  <input id={id} value={host} placeholder="geo.iproyal.com" onChange={(e) => setHost(e.target.value)} className={inputClass} />
                )}
              </Field>
            </div>
            <Field label="Porta">
              {(id) => (
                <input
                  id={id}
                  inputMode="numeric"
                  value={port}
                  placeholder="11200"
                  onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, ""))}
                  className={inputClass}
                />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo">
              {(id) => (
                <select id={id} value={type} onChange={(e) => setType(e.target.value as "SOCKS5" | "HTTP" | "HTTPS")} className={inputClass}>
                  {(["SOCKS5", "HTTP", "HTTPS"] as const).map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Fornitore" hint="Decide come si compongono i parametri.">
              {(id) => (
                <input id={id} value={provider} placeholder="iproyal" onChange={(e) => setProvider(e.target.value)} className={inputClass} />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Username">
              {(id) => <input id={id} value={username} autoComplete="off" onChange={(e) => setUsername(e.target.value)} className={inputClass} />}
            </Field>
            <Field
              label="Password"
              check="Password del proxy"
              hint={
                proxy
                  ? "Vuoto: resta quella di adesso."
                  : "Il segreto (i parametri qui sotto), oppure la stringa sticky del pannello IPRoyal, che si usa com'è."
              }
            >
              {(id) => (
                <input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                />
              )}
            </Field>
          </div>
          {passwordIssue && <p className="text-xs text-loss">{passwordIssue}</p>}
          {proxy && isAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              {shownPassword === null ? (
                <Button size="sm" variant="ghost" disabled={revealing} onClick={() => void mostraPassword()}>
                  {revealing ? "Lettura…" : "Mostra password"}
                </Button>
              ) : (
                <code className="machine-id select-all break-all rounded border border-line bg-raised px-2 py-1 text-sm text-ink">{shownPassword}</code>
              )}
              {revealError && <span className="text-xs text-loss">{revealError}</span>}
            </div>
          )}
          {sticky ? (
            <p className="text-xs text-muted">
              <StickyLine sticky={sticky} />
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Paese">
                  {(id) => <input id={id} value={country} placeholder="IT" onChange={(e) => setCountry(e.target.value)} className={inputClass} />}
                </Field>
                <Field label="Città">
                  {(id) => <input id={id} value={city} placeholder="Milan" onChange={(e) => setCity(e.target.value)} className={inputClass} />}
                </Field>
              </div>
              {proxy?.sessionId && (
                <p className="text-xs text-faint">
                  Sessione <span className="machine-id">{proxy.sessionId}</span>: è il suffisso che decide l'indirizzo d'uscita. Deriva dal
                  cliente e non si condivide con nessun altro.
                </p>
              )}
            </>
          )}
          {proxyInvalid && <p className="text-xs text-loss">Per un proxy servono host e una porta valida.</p>}
          {proxyWanted && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Button size="sm" variant="ghost" disabled={checking || proxyInvalid || !!passwordIssue} onClick={() => void controllaProxy()}>
                {checking ? "Controllo…" : "Controlla"}
              </Button>
              {proxyCheck && (
                <span className={proxyCheck.ok ? "text-gain" : "text-loss"}>
                  {proxyCheck.detail} · {proxyCheck.elapsedMs} ms
                </span>
              )}
              {proxyCheck?.warning && <span className="text-warn">{proxyCheck.warning}</span>}
            </div>
          )}
          {stale && (
            <div role="status" className="space-y-2 rounded border border-line px-3 py-2 text-sm">
              {stale.count > 0 && (
                <p className="text-warn">
                  {stale.count === 1 ? "1 conto usa" : `${stale.count} conti usano`} ancora il proxy precedente.
                </p>
              )}
              {stale.esito && <p className="text-ink">{stale.esito}</p>}
              {stale.count > 0 && (
                <Button size="sm" variant="ghost" disabled={reconnecting} onClick={() => void ricollega()}>
                  {reconnecting ? "Ricollego…" : "Ricollega ora"}
                </Button>
              )}
            </div>
          )}
        </fieldset>

        <Field label="Note">
          {(id) => <textarea id={id} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />}
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </form>
      </FormCheck>
    </SidePanel>
  );
}

// ---------------------------------------------------------------------------
// Conto
// ---------------------------------------------------------------------------

/**
 * Un conto MT5. CID e proxy NON ci sono: sono del cliente (DECISIONE 19). Qui
 * si mostra soltanto con che cosa il conto andra' a mercato, in sola lettura —
 * un secondo posto dove scriverli sarebbe un posto da cui un giorno esce un
 * valore diverso da quello che il gestionale mostra.
 */
export function AccountPanel({
  open,
  clientId,
  clientDefaults,
  account,
  defaultRole = "PROP",
  onClose,
}: {
  open: boolean;
  clientId: string;
  clientDefaults: { computerId: string; defaultProxy: ProxyPublic | null };
  account: Account | null;
  defaultRole?: "PROP" | "BROKER" | "NONE";
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const firms = usePropFirms();
  const brokers = useBrokers();
  const isClient = useIsClient();

  const [label, setLabel] = useState("");
  const [role, setRole] = useState<"PROP" | "BROKER" | "NONE">(defaultRole);
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  /**
   * La password COSI' COM'ERA quando il pannello si e' aperto, letta da
   * `reveal-password` (gia' auditata li'). Serve a capire se
   * `password` e' stata davvero cambiata: senza, riaprire il pannello e
   * salvare senza toccare il campo manderebbe la stessa password come se
   * fosse nuova — disconnessione e riconnessione per niente.
   */
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);
  const [serverId, setServerId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [notes, setNotes] = useState("");

  const [newServer, setNewServer] = useState(false);
  const [serverName, setServerName] = useState("");
  /**
   * Il possessore del server (prop firm o broker), scelto PRIMA del server: il
   * menu mostra solo i server legati a lui, e un server nuovo nasce sotto di lui.
   */
  const [ownerId, setOwnerId] = useState("");
  /**
   * Nomi simili quando il gateway non ha trovato `serverName` (`SERVER_NOT_ON_GATEWAY`).
   * Cliccabili: riempiono il campo invece di far ridigitare a mano.
   */
  const [serverSuggestions, setServerSuggestions] = useState<string[]>([]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Il conto che tiene il numero MT5 gia' usato (409 LOGIN_TAKEN): vedi `AccountConflictNote`.
  const [conflict, setConflict] = useState<AccountConflict | null>(null);
  const check = useFormCheck([
    ["Etichetta", label.trim() !== ""],
    ["Login MT5", login.trim() !== ""],
    ["Valuta", currency.trim() !== ""],
    ["Password MT5", !!account || password !== ""],
    !newServer && ["Server MT5", serverId !== ""],
    newServer && ["Nome esatto del server MT5", serverName.trim() !== ""],
  ]);
  const resetCheck = check.reset;

  // Le fee pagate del ciclo del conto prop, una per riga di registro (per id di riga). Un campo non toccato non ha voce: si mostra quella registrata.
  const cycleFee = useCycleFee(account, open);
  const [feeInput, setFeeInput] = useState<Record<string, string>>({});
  const [feeReason, setFeeReason] = useState<Record<string, string>>({});
  /** Gli importi appena scritti: finche' la lista non si e' riletta, il paragone e' con loro e non con la riga vecchia. */
  const [feeSaved, setFeeSaved] = useState<Record<string, string>>({});

  /*
   * DIPENDE DALL'ID DEL CONTO, NON DALL'OGGETTO — e non e' un dettaglio di
   * React.
   *
   * `account` arriva da una query: dopo ogni `refresh()` e' un oggetto NUOVO
   * anche quando i suoi valori sono gli stessi, quindi questo effetto ripartiva
   * a ogni salvataggio e azzerava tutto — compreso `error`, cioe' proprio
   * l'avviso «salvato, ma la riconnessione non e' riuscita», che quindi non si
   * vedeva mai e lasciava il conto giu' senza dirlo. Ripartiva anche l'effetto
   * qui sotto, con una riga di audit di lettura password che nessuno aveva
   * chiesto.
   *
   * Il pannello si ricarica quando si apre o quando cambia CONTO: sono i due
   * casi in cui i campi devono tornare quelli della riga.
   */
  useEffect(() => {
    if (!open) return;
    setLabel(account?.label ?? "");
    setRole(account?.role ?? defaultRole);
    setLogin(account?.mt5Login ?? "");
    setPassword("");
    setRevealedPassword(null);
    setServerId(account?.serverId ?? "");
    setCurrency(account?.currency ?? (defaultRole === "BROKER" ? "EUR" : "USD"));
    setNotes(account?.notes ?? "");
    setNewServer(false);
    setServerName("");
    setOwnerId("");
    setServerSuggestions([]);
    setError(null);
    setConflict(null);
    setFeeInput({});
    setFeeReason({});
    setFeeSaved({});
    resetCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- l'id, non l'oggetto: vedi sopra
  }, [open, account?.id, defaultRole]);

  /*
   * In modifica il possessore parte da quello del server del conto. Effetto a
   * parte perche' la lista dei server puo' arrivare dopo l'apertura, e questo
   * non deve azzerare gli altri campi: scrive solo un possessore ancora vuoto,
   * e solo finche' il server e' quello della riga. Un possessore archiviato non
   * si sceglie: resta vuoto, e il server attuale si mostra a parte (sotto).
   */
  useEffect(() => {
    if (!open || !account || ownerId || serverId !== account.serverId) return;
    const owner = servers.data?.find((s) => s.id === account.serverId)?.propFirmId;
    if (owner && [...(firms.data ?? []), ...(brokers.data ?? [])].some((o) => o.id === owner)) setOwnerId(owner);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- l'id del conto e le liste, non l'oggetto
  }, [open, account?.id, servers.data, firms.data, brokers.data, serverId]);

  /**
   * LA PASSWORD IN CHIARO: si rilegge da `reveal-password` (nessuna rotta
   * nuova, e' quella che gia' esiste per «Mostra password») a ogni apertura del
   * pannello su un conto che c'e' gia'. La vedono tutti gli interni, **mai il
   * CLIENT** — che qui non arriva e che la rotta rifiuta comunque — e mai le
   * liste: `GET /accounts` non la porta, e' questa chiamata a portarla.
   */
  useEffect(() => {
    if (!open || !account || isClient) return;
    let annullato = false;
    api.accounts
      .revealPassword(account.id)
      .then((r) => {
        if (annullato) return;
        /*
         * IL CAMPO SI RIEMPIE SOLO SE NESSUNO L'HA ANCORA TOCCATO.
         *
         * La lettura e' una chiamata di rete: chi apre il pannello e comincia
         * subito a scrivere la password nuova si vedeva sostituire quello che
         * aveva scritto dalla vecchia — e siccome a quel punto il campo era
         * IDENTICO alla riletta, il salvataggio non mandava nessuna password.
         * La modifica spariva in silenzio, che e' il modo peggiore di perderla.
         *
         * Il confronto si fa nella forma funzionale perche' qui lo stato di
         * adesso e' l'unico che dice se nel frattempo e' stato battuto un tasto.
         */
        setPassword((digitata) => (digitata === "" ? r.password : digitata));
        // La riletta resta comunque il PARAGONE: serve a sapere se quello che
        // si sta per mandare e' davvero diverso da quello che c'e' nel database.
        setRevealedPassword(r.password);
      })
      .catch(() => {
        /* Il campo resta vuoto: si può comunque scriverne una nuova. */
      });
    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- l'id: una rilettura per apertura, non per rinfresco
  }, [open, account?.id, isClient]);

  const editing = !!account;
  const locked = !!account?.isConnected; // il ruolo si cambia solo a conto disconnesso
  const kind = role === "NONE" ? null : role;
  const owners = kind === "PROP" ? (firms.data ?? []) : kind === "BROKER" ? (brokers.data ?? []) : [...(firms.data ?? []), ...(brokers.data ?? [])];
  const ownerWord = kind === "PROP" ? "la prop firm" : kind === "BROKER" ? "il broker" : "la prop firm o il broker";
  // Solo i server del possessore scelto, e del tipo del ruolo; la lista arriva gia' senza archiviati ne' eliminati.
  const serverOptions = (servers.data ?? []).filter((s) => ownerId !== "" && s.propFirmId === ownerId && (!kind || s.kind === kind));
  /*
   * IN MODIFICA, IL SERVER ATTUALE DI UN POSSESSORE ARCHIVIATO (o senza
   * possessore) non sparisce: resta nel menu, selezionato, con la nota — cosi'
   * il conto non sembra senza server, e ci si torna dopo aver guardato altrove.
   */
  const currentServer = editing ? servers.data?.find((s) => s.id === account.serverId) : undefined;
  const activeOwnerIds = new Set([...(firms.data ?? []), ...(brokers.data ?? [])].map((o) => o.id));
  const keepCurrent = !!currentServer && (!kind || currentServer.kind === kind) && !activeOwnerIds.has(currentServer.propFirmId ?? "");
  const currentNote = currentServer?.propFirmId ? "prop firm/broker archiviato" : "senza prop firm/broker";

  // Il proxy del cliente vale solo sui conti prop.
  const proxyInUso = role === "PROP" ? clientDefaults.defaultProxy : null;
  const propWithoutProxy = role === "PROP" && !clientDefaults.defaultProxy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!check.attempt()) return;
    setBusy(true);
    setError(null);
    setConflict(null);
    setServerSuggestions([]);
    try {
      /*
       * LE FEE DEL CICLO, per prime: passano dalla STESSA rotta con cui i
       * movimenti del cliente correggono una riga (`PATCH /transactions/:id`),
       * che rifa' fee, payout e «Da recuperare» nella sua transazione. Prima si
       * controllano TUTTE (importo letto, motivo scritto), poi si scrive riga
       * per riga: un campo sbagliato non lascia le altre righe a meta'. Se una
       * scrittura viene rifiutata non e' ancora nato nessun server e non e'
       * stato toccato il conto: l'errore resta nella modale e si riprova.
       */
      if (editing && cycleFee.kind === "editable") {
        const daScrivere: { id: string; value: string; reason: string }[] = [];
        for (const line of cycleFee.lines) {
          const scritto = feeInput[line.row.id];
          if (line.locked || scritto === undefined) continue;
          const titolo = `${line.row.notes?.trim() || "Fee senza nota"}`;
          const p = parseDecimal(scritto);
          if (p.kind !== "value") throw new Error(`${titolo}: ${p.kind === "error" ? p.message : "scrivi un importo"}`);
          if (Math.round(Number(p.value) * 100) === Math.round(Number(feeBaseOf(line, feeSaved)) * 100)) continue;
          const reason = (feeReason[line.row.id] ?? "").trim();
          if (reason.length < 3) throw new Error(`${titolo}: scrivi il motivo della modifica`);
          daScrivere.push({ id: line.row.id, value: p.value, reason });
        }
        for (const w of daScrivere) {
          await api.ledger.update(w.id, { reason: w.reason, amount: `-${w.value}` });
          setFeeSaved((s) => ({ ...s, [w.id]: Number(w.value).toFixed(2) }));
          setFeeInput((s) => Object.fromEntries(Object.entries(s).filter(([k]) => k !== w.id)));
          setFeeReason((s) => Object.fromEntries(Object.entries(s).filter(([k]) => k !== w.id)));
        }
      }

      // Il server nuovo, se serve, e' lo STESSO fieldset per creazione e
      // modifica: passa gia' dalla verifica col gateway, non si tocca qui.
      let sid = serverId;
      if (newServer) {
        if (!kind) throw new Error("Scegli il ruolo prima di creare il server: prop e broker usano server diversi");
        if (!ownerId) throw new Error(`Scegli prima ${ownerWord}: il server nuovo nasce legato a lui`);
        const created = await api.servers.create({
          name: serverName.trim(),
          kind,
          propFirmId: ownerId,
        });
        sid = created.id;
        /*
         * IL SERVER E' NATO: da qui in poi e' uno della lista, e il form deve
         * saperlo SUBITO.
         *
         * Se il salvataggio qui sotto fallisce — login gia' preso, riconnessione
         * non riuscita — il pannello resta aperto: senza queste due righe
         * restava anche in modalita' «nuovo server», e premere Salva di nuovo
         * ripeteva la POST sullo stesso nome (409 «Esiste già un server con
         * questo nome») mentre il server appena creato non era nemmeno
         * selezionabile, perche' la tendina era nascosta.
         */
        setNewServer(false);
        setServerId(created.id);
        await refresh();
      }

      if (editing) {
        // Solo ciò che è cambiato.
        const patch: AccountPatch = {};
        if (label.trim() !== account.label) patch.label = label.trim();
        if (role !== account.role) patch.role = role;
        if (login.trim() !== account.mt5Login) patch.mt5Login = login.trim();
        if (sid !== account.serverId) patch.serverId = sid;
        // Confrontata con quella RILETTA all'apertura, non con vuoto: senza,
        // riaprire il pannello e salvare senza toccarla manderebbe la stessa
        // password come se fosse nuova.
        if (password && password !== (revealedPassword ?? "")) patch.mt5Password = password;
        if (currency !== account.currency) patch.currency = currency;
        if (orNull(notes) !== account.notes) patch.notes = orNull(notes);
        if (Object.keys(patch).length) {
          const salvato = await api.accounts.update(account.id, patch);
          if (salvato.reconnectError) {
            /*
             * Salvato per davvero: si dice che la riconnessione non e' riuscita
             * invece di far finta che sia andato tutto liscio — il conto resta
             * disconnesso, si riconnette a mano da qui.
             *
             * L'avviso si scrive PRIMA del `refresh()`: quello rinfresca la
             * query da cui arriva `account`, e con l'effetto agganciato
             * all'oggetto il messaggio spariva un istante dopo essere comparso.
             */
            setError(`Salvato, ma la riconnessione non è riuscita: ${salvato.reconnectError}. Riconnetti manualmente dalla scheda del conto.`);
            setBusy(false);
            await refresh();
            return;
          }
        }
      } else {
        await api.accounts.create({
          label: label.trim(),
          clientId,
          role,
          mt5Login: login.trim(),
          mt5Password: password,
          serverId: sid,
          currency,
          notes: orNull(notes),
        });
      }
      await refresh();
      onClose();
    } catch (err) {
      await refresh(); // un server creato prima dell'errore deve comparire nella tendina
      // Il server non risulta sul gateway: si propongono i nomi simili invece
      // di far ridigitare a caso, e il conto non si e' salvato (la creazione
      // del server e' fallita prima).
      if (err instanceof ApiError && err.code === "SERVER_NOT_ON_GATEWAY") setServerSuggestions(err.suggestions ?? []);
      if (err instanceof ApiError && err.conflict) setConflict(err.conflict);
      setError(errorMessage(err));
      check.fromError(err, {
        label: "Etichetta",
        mt5Login: "Login MT5",
        LOGIN_TAKEN: "Login MT5",
        mt5Password: "Password MT5",
        currency: "Valuta",
        serverId: "Server MT5",
        name: "Nome esatto del server MT5",
        SERVER_NOT_ON_GATEWAY: "Nome esatto del server MT5",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={editing ? `Modifica ${account.label}` : "Nuovo conto"}
      subtitle={
        locked
          ? "Conto connesso: il ruolo si cambia solo dopo averlo disconnesso. Login, server e password si possono cambiare — il conto si disconnette e si riconnette da solo."
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="account-form" disabled={busy}>
            {busy ? "Salvataggio…" : editing ? "Salva" : "Aggiungi conto"}
          </Button>
          <MissingNote check={check} end />
        </>
      }
    >
      <FormCheck check={check}>
      <form id="account-form" noValidate onSubmit={submit} className="space-y-4">
        {conflict && error && <AccountConflictNote message={error} conflict={conflict} onOpen={onClose} />}
        <Field label="Ruolo">
          {(id) => (
            <div id={id} role="radiogroup" className="grid grid-cols-3 gap-2">
              {(["PROP", "BROKER", "NONE"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={role === r}
                  disabled={locked}
                  onClick={() => {
                    // Un server della kind sbagliata non e' piu' una scelta
                    // valida: si azzera anche in modifica, ora che il server
                    // si può cambiare — ma solo se il ruolo cambia DAVVERO,
                    // non a ogni clic sul pulsante già scelto.
                    if (r !== role) {
                      setServerId("");
                      setOwnerId("");
                    }
                    setRole(r);
                  }}
                  className={[
                    "rounded border px-2 py-1.5 text-sm transition-colors disabled:opacity-50",
                    role === r
                      ? r === "PROP"
                        ? "border-prop bg-prop/15 text-prop"
                        : r === "BROKER"
                          ? "border-broker bg-broker/15 text-broker"
                          : "border-muted bg-raised text-ink"
                      : "border-line text-muted hover:text-ink",
                  ].join(" ")}
                >
                  {r === "PROP" ? "Prop" : r === "BROKER" ? "Broker" : "Nessun ruolo"}
                </button>
              ))}
            </div>
          )}
        </Field>

        <Field label="Etichetta">
          {(id) => (
            <input id={id} required value={label} placeholder="nastasi next1" onChange={(e) => setLabel(e.target.value)} className={inputClass} />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Login MT5">
            {(id) => (
              <input
                id={id}
                required
                inputMode="numeric"
                value={login}
                onChange={(e) => setLogin(e.target.value.replace(/\D/g, ""))}
                className={`${inputClass} font-mono`}
              />
            )}
          </Field>
          <Field label="Valuta" hint="Verificata alla connessione: se il server ne dice un'altra, un conto senza storico si corregge da solo.">
            {(id) => (
              <input
                id={id}
                required
                maxLength={3}
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                className={inputClass}
              />
            )}
          </Field>
        </div>

        <Field label="Password MT5" hint="Cifrata prima di essere salvata. Ogni lettura in chiaro lascia una riga di audit col tuo nome.">
          {(id) => (
            <input
              id={id}
              type="text"
              required={!editing}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          )}
        </Field>

        <Field label={kind === "PROP" ? "Prop firm" : kind === "BROKER" ? "Broker" : "Prop firm o broker"}>
          {(id) => (
            <select
              id={id}
              value={ownerId}
              onChange={(e) => {
                setOwnerId(e.target.value);
                // Il server di un altro possessore non resta scelto di nascosto.
                setServerId("");
              }}
              className={inputClass}
            >
              <option value="" disabled>
                {owners.length ? `Scegli ${ownerWord}` : "Nessuno nel catalogo"}
              </option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        {!newServer ? (
          <Field
            label="Server MT5"
            hint={
              keepCurrent && serverId === currentServer?.id ? (
                `Server attuale, ${currentNote}: resta questo finché non ne scegli un altro.`
              ) : ownerId ? (
                <button type="button" className="text-muted underline-offset-2 hover:text-ink hover:underline" onClick={() => setNewServer(true)}>
                  Il server non è in elenco? Aggiungilo
                </button>
              ) : undefined
            }
          >
            {(id) => (
              <select id={id} required value={serverId} disabled={!ownerId && !keepCurrent} onChange={(e) => setServerId(e.target.value)} className={inputClass}>
                <option value="" disabled>
                  {!ownerId ? `Scegli prima ${ownerWord}` : serverOptions.length ? "Scegli il server" : "Nessun server: aggiungilo"}
                </option>
                {keepCurrent && currentServer && (
                  <option value={currentServer.id}>
                    {currentServer.displayName ?? currentServer.name} · {currentNote}
                  </option>
                )}
                {serverOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.displayName ?? s.name}
                    {s.propFirm ? ` · ${s.propFirm.name}` : ""}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <fieldset className="space-y-3 rounded border border-line p-3">
            <legend className="px-1 text-sm text-muted">Nuovo server {kind === "PROP" ? "prop" : kind === "BROKER" ? "broker" : ""}</legend>
            <Field label="Nome esatto del server MT5" hint="Come compare nel terminale, per esempio FundedNext-Server 2. Si verifica col gateway prima di salvare.">
              {(id) => (
                <input
                  id={id}
                  required
                  value={serverName}
                  onChange={(e) => {
                    setServerName(e.target.value);
                    setServerSuggestions([]);
                  }}
                  className={inputClass}
                />
              )}
            </Field>
            {serverSuggestions.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted">Non trovato. Forse uno di questi:</p>
                <div className="flex flex-wrap gap-2">
                  {serverSuggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="rounded border border-line px-2 py-1 text-xs text-ink hover:bg-raised"
                      onClick={() => {
                        setServerName(s);
                        setServerSuggestions([]);
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button type="button" className="text-sm text-muted hover:text-ink" onClick={() => setNewServer(false)}>
              Scegli un server esistente
            </button>
          </fieldset>
        )}

        {/* CID e proxy sono del CLIENTE: qui si legge con che cosa il conto va a
            mercato, e si cambia dalla scheda del cliente. */}
        <div className="rounded border border-line bg-raised/40 p-3 text-sm">
          <p className="text-xs text-faint">Con che cosa va a mercato</p>
          <p className="mt-1">
            CID{" "}
            {clientDefaults.computerId ? (
              <span className="machine-id">{clientDefaults.computerId}</span>
            ) : (
              <span className="text-muted">nessuno: lo genera il gateway a ogni accesso</span>
            )}
          </p>
          <p>
            Proxy{" "}
            {proxyInUso ? (
              <span className="text-ink">
                {proxyName(proxyInUso)}
                {[proxyInUso.city, proxyInUso.country].filter(Boolean).length
                  ? ` · ${[proxyInUso.city, proxyInUso.country].filter(Boolean).join(", ")}`
                  : ""}
              </span>
            ) : role === "BROKER" ? (
              <span className="text-muted">nessuno: la gamba broker non passa dal proxy</span>
            ) : (
              <span className="text-warn">nessuno: senza, il conto prop non si connette</span>
            )}
          </p>
          <p className="mt-2 text-xs text-faint">
            Sono del cliente e si cambiano dalla sua scheda. Con un ciclo aperto il CID è quello congelato all'apertura.
          </p>
          {propWithoutProxy && (
            <p className="mt-1 text-xs text-warn">Assegna un proxy al cliente prima di provare a connettere il conto prop.</p>
          )}
        </div>

        {/* La variante si guarda sul server del conto com'e' salvato: cambiare server la azzera, e si rileva alla connessione. */}
        {account && !isClient && account.serverId === serverId && <SymbolVariantSection account={account} />}

        {account && (
          <CycleFeeField
            view={cycleFee}
            clientId={account.clientId}
            edits={feeInput}
            saved={feeSaved}
            onValue={(id, v) => setFeeInput((p) => ({ ...p, [id]: v }))}
            reasons={feeReason}
            onReason={(id, v) => setFeeReason((p) => ({ ...p, [id]: v }))}
          />
        )}

        <Field label="Note">
          {(id) => <textarea id={id} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />}
        </Field>

        {!conflict && <ErrorNote>{error}</ErrorNote>}
      </form>
      </FormCheck>
    </SidePanel>
  );
}

/**
 * LA FEE PAGATA DEL CICLO DEL CONTO PROP, vista dalla modale Modifica.
 *
 * Non c'e' logica nuova: il ciclo del conto si ricava dalle Fasi del cliente
 * (la piu' recente che ha questo conto come prop, preferendo un ciclo in
 * corso), la riga di fee dal registro del cliente, e la scrittura e' la
 * `PATCH /transactions/:id` dei movimenti — solo ADMIN, motivo obbligatorio.
 *
 * **Nessuna riga e' «la challenge»**: le righe non portano un segno
 * strutturale che lo dica (c'e' solo il testo delle note), e un testo non e'
 * una chiave. Si mostrano TUTTE le righe `PROP_FEE` del ciclo, dalla piu'
 * vecchia, ciascuna con la sua nota e la sua data, e ciascuna si corregge per
 * conto suo. Una riga con uno storno collegato resta di sola lettura.
 */
type FeeLine = {
  row: LedgerRow;
  /** Importo scritto come lo legge chi guarda (positivo), nella valuta della riga. */
  amount: string;
  /** Il perche' la riga non si tocca da qui; `null` = si modifica. */
  locked: string | null;
};

type CycleFeeView =
  | { kind: "none" }
  | { kind: "loading" }
  | { kind: "readonly"; why: string; amount: string | null; currency: string }
  | { kind: "editable"; lines: FeeLine[] };

function useCycleFee(account: Account | null, open: boolean): CycleFeeView {
  const isAdmin = useIsAdmin();
  const isClient = useIsClient();
  const on = open && !!account && account.role === "PROP" && !isClient;
  const clientId = account?.clientId ?? "";
  // Stessa chiave di `useSessions({ clientId })`: la lista e' quella che c'e' gia' in cache.
  const sessions = useQuery({ queryKey: keys.sessionList({ clientId }), queryFn: () => api.sessions.list({ clientId }), enabled: on });

  const rank = (state: string | undefined) => (state === "ACTIVE" ? 0 : 1);
  const cycle =
    (sessions.data ?? [])
      .filter((s) => s.propAccountId === account?.id && s.cycle !== null)
      .sort((a, b) => rank(a.cycle?.state) - rank(b.cycle?.state) || b.startedAt.localeCompare(a.startedAt))[0]?.cycle ?? null;

  const editable = on && !!cycle && cycle.state === "ACTIVE" && isAdmin;
  // Il ciclo in query key: un ciclo diverso e' una lista diversa. Sotto `["ledger", cliente]`, cosi' ogni scrittura sui movimenti la rilegge.
  const ledger = useQuery({
    queryKey: [...keys.ledger(clientId), "cycle-fee", cycle?.id ?? ""],
    queryFn: () => api.ledger.list(clientId, { kind: "PROP_FEE", cycleId: cycle!.id }),
    enabled: editable,
  });

  if (!on) return { kind: "none" };
  if (sessions.isError) return { kind: "readonly", why: `Non riesco a leggere il ciclo del conto: ${errorMessage(sessions.error)}`, amount: null, currency: "" };
  if (sessions.isPending) return { kind: "loading" };
  if (!cycle) return { kind: "none" };

  const fromCycle = { amount: String(cycle.feesPaid), currency: cycle.currency };
  if (cycle.state !== "ACTIVE") {
    return { kind: "readonly", why: "Il ciclo non è più in corso: i suoi numeri sono congelati e le sue righe non si toccano più.", ...fromCycle };
  }
  if (!isAdmin) return { kind: "readonly", why: "La fee la modifica solo un amministratore.", ...fromCycle };

  if (ledger.isError) return { kind: "readonly", why: `Non riesco a leggere le righe di fee: ${errorMessage(ledger.error)}`, ...fromCycle };
  if (ledger.isPending) return { kind: "loading" };
  const rows = ledger.data.rows
    .filter((r) => r.kind === "PROP_FEE")
    .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt) || a.createdAt.localeCompare(b.createdAt));
  if (rows.length === 0) return { kind: "readonly", why: "Il registro non ha una riga di fee per questo ciclo: la fee si registra dai movimenti del cliente.", ...fromCycle };
  return {
    kind: "editable",
    lines: rows.map((row) => ({
      row,
      amount: Math.abs(Number(row.amount)).toFixed(2),
      locked: row.reversedBy || row.reversesId || row.payoutId ? "Ha uno storno collegato: si corregge dai movimenti del cliente." : null,
    })),
  };
}

/** Quanto vale la riga adesso: l'ultimo importo salvato da questa modale, se la lista non si e' ancora riletta, o quello registrato. */
const feeBaseOf = (line: FeeLine, saved: Record<string, string>) => saved[line.row.id] ?? line.amount;

function CycleFeeField({
  view,
  clientId,
  edits,
  saved,
  onValue,
  reasons,
  onReason,
}: {
  view: CycleFeeView;
  clientId: string;
  /** Quello che si e' scritto nei campi toccati, per id di riga: assente = non toccato. */
  edits: Record<string, string>;
  saved: Record<string, string>;
  onValue: (rowId: string, v: string) => void;
  reasons: Record<string, string>;
  onReason: (rowId: string, v: string) => void;
}) {
  if (view.kind === "none") return null;
  if (view.kind === "loading") return <p className="text-xs text-faint">Leggo la fee del ciclo…</p>;
  if (view.kind === "readonly") {
    return (
      <div className="rounded border border-line bg-raised/40 p-3 text-sm">
        <p className="text-xs text-faint">Fee pagata del ciclo</p>
        {view.amount !== null && <p className="mt-1 text-ink">{money(view.amount, view.currency)}</p>}
        <p className="mt-1 text-xs text-muted">{view.why}</p>
        <Link to={`/clienti/${clientId}/movimenti`} className="mt-1 inline-block text-xs text-muted underline-offset-2 hover:text-ink hover:underline">
          Apri i movimenti del cliente
        </Link>
      </div>
    );
  }
  return (
    <fieldset className="space-y-3 rounded border border-line p-3">
      <legend className="px-1 text-sm text-muted">Fee pagate del ciclo in corso</legend>
      <p className="text-xs text-faint">Sono le righe di fee del registro, le stesse dei movimenti del cliente: «Da recuperare» si rifà da sola.</p>
      {view.lines.map((line) => {
        const { row } = line;
        const titolo = `${row.notes?.trim() || "Fee senza nota"} · ${date(row.occurredAt)}`;
        if (line.locked) {
          return (
            <div key={row.id} className="text-sm">
              <p className="text-xs text-muted">{titolo}</p>
              <p className="text-ink">{money(line.amount, row.currency)}</p>
              <p className="text-xs text-faint">{line.locked}</p>
              <Link to={`/clienti/${clientId}/movimenti`} className="text-xs text-muted underline-offset-2 hover:text-ink hover:underline">
                Apri i movimenti del cliente
              </Link>
            </div>
          );
        }
        return (
          <div key={row.id} className="space-y-2 border-t border-line/60 pt-3 first:border-t-0 first:pt-0">
            <Field label={`${titolo} (${row.currency})`}>
              {(id) => (
                <input id={id} inputMode="decimal" value={edits[row.id] ?? feeBaseOf(line, saved)} onChange={(e) => onValue(row.id, e.target.value)} className={`${inputClass} text-right`} />
              )}
            </Field>
            <Field label="Motivo della modifica" hint="Obbligatorio se cambi questa fee: finisce nel registro delle modifiche, col prima e il dopo.">
              {(id) => <input id={id} value={reasons[row.id] ?? ""} onChange={(e) => onReason(row.id, e.target.value)} className={inputClass} />}
            </Field>
          </div>
        );
      })}
    </fieldset>
  );
}

/** `-E` cosi' com'e', la stringa vuota a parole. */
const variantName = (v: string) => (v === "" ? "senza suffisso" : v);

/**
 * LA VARIANTE DEI SIMBOLI DEL CONTO (catalogo del 09/10 sera).
 *
 * Sul server FPG un conto vede `XAUUSD-E` o `XAUUSD-P`: la variante dice quale
 * riga del catalogo usa, per tutte le sue coppie. Si rileva alla prima
 * connessione; quando il conto ne vede due la domanda e' qui — «Quale tipo usa
 * questo conto?» — e finche' non c'e' una risposta il conto non apre. Si
 * sceglie fra le varianti del catalogo del suo server, **senza preselezione**
 * quando la variante non c'e'; con operazioni aperte il backend rifiuta.
 */
function SymbolVariantSection({ account }: { account: Account }) {
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const [scelta, setScelta] = useState<string | null>(account.symbolVariant);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fatto, setFatto] = useState(false);
  useEffect(() => {
    setScelta(account.symbolVariant);
    setError(null);
    setFatto(false);
  }, [account.id, account.symbolVariant]);

  const righe = servers.data?.find((x) => x.id === account.serverId)?.symbols ?? [];
  // Le varianti del catalogo, ciascuna coi suoi nomi: «-E (XAUUSD-E, EURUSD-E)».
  const varianti = [...new Set(righe.map((m) => m.variant ?? ""))].sort().map((v) => ({
    v,
    nomi: righe.filter((m) => (m.variant ?? "") === v).map((m) => m.brokerSymbol),
  }));
  const daScegliere = account.symbolVariant === null && account.symbolVariantOptions.length > 1;
  const quando = account.symbolVariantAt ? new Date(account.symbolVariantAt).toLocaleDateString("it-IT") : null;
  const origine = { DETECTED: "rilevata alla connessione", MANUAL: "scelta a mano", MIGRATED: "portata dalle righe del conto" } as const;

  const salva = async () => {
    if (scelta === null) return setError("Scegli la variante.");
    setBusy(true);
    setError(null);
    setFatto(false);
    try {
      await api.accounts.setSymbolVariant(account.id, scelta);
      await refresh();
      setFatto(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded border p-3 text-sm ${daScegliere ? "border-warn bg-warn/10" : "border-line bg-raised/40"}`}>
      <p className="text-xs text-faint">Variante dei simboli</p>
      {account.symbolVariant !== null ? (
        <p className="mt-1">
          <span className="font-mono text-ink">{variantName(account.symbolVariant)}</span>
          <span className="text-muted">
            {account.symbolVariantSource ? ` · ${origine[account.symbolVariantSource]}` : ""}
            {quando ? ` il ${quando}` : ""}
          </span>
        </p>
      ) : daScegliere ? (
        <p className="mt-1 text-warn">
          <span className="font-medium">Quale tipo usa questo conto?</span> Vede più varianti dello stesso simbolo (
          {account.symbolVariantOptions.map(variantName).join(", ")}): finché non la scegli non apre operazioni.
        </p>
      ) : account.symbolVariantAt === null ? (
        <p className="mt-1 text-muted">Non ancora rilevata: si rileva al prossimo «Connetti».</p>
      ) : (
        <p className="mt-1 text-warn">Il conto non vede nessuna variante del catalogo del suo server: scegli la sua.</p>
      )}

      {varianti.length > 0 && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <Field label="Variante" hint="Vale per tutte le coppie del conto. Con operazioni aperte non si cambia.">
            {(id) => (
              <select id={id} value={scelta ?? "__nessuna__"} onChange={(e) => setScelta(e.target.value === "__nessuna__" ? null : e.target.value)} className={inputClass}>
                {scelta === null && <option value="__nessuna__">Scegli la variante</option>}
                {varianti.map(({ v, nomi }) => (
                  <option key={v} value={v}>
                    {variantName(v)} ({nomi.join(", ")}){account.symbolVariantOptions.includes(v) ? " · vista dal conto" : ""}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Button type="button" size="sm" disabled={busy || scelta === null || scelta === account.symbolVariant} onClick={salva}>
            {busy ? "Salvo…" : "Salva variante"}
          </Button>
        </div>
      )}
      {fatto && <p className="mt-1 text-xs text-muted">Variante salvata.</p>}
      <ErrorNote>{error}</ErrorNote>
    </div>
  );
}

/**
 * IL PROXY STICKY IPROYAL IN UNA RIGA: «Sticky IPRoyal · sessione <id> · IP
 * fisso <durata>». La stessa nel modulo e nella scheda del cliente.
 */
export function StickyLine({ sticky }: { sticky: { session: string; lifetime: string | null } }) {
  return (
    <>
      Sticky IPRoyal · sessione <span className="machine-id">{sticky.session}</span>
      {sticky.lifetime ? ` · IP fisso ${sticky.lifetime}` : ""}
    </>
  );
}
